import {useEffect,useMemo,useState,useRef,type PointerEvent} from 'react';
import {localBookingTimeToUTC} from './SchedulingWorkspace';
type Location={code:string;name:string;timezone:string};
type Shift={shiftId:string;actorId:string|null;staffName:string;roleName:string;
  startsAt:string;endsAt:string;shiftColor:string|null;publishedAt?:string|null};
type StaffChoice={actorId:string;name:string;color:string|null;
  status:'AVAILABLE'|'IF_NEEDED'|'UNAVAILABLE'};
type AvailWindow=Availability['windows'][number];
type Drag={kind:'shift'|'availability'|'shift-resize'|'availability-resize';
  dayIndex:number;startMinute:number;endMinute:number;
  shift?:Shift;window?:AvailWindow;};
type ShiftsResponse={location:string;timeZone:string;shifts:Shift[];canManageShifts:boolean};
type Availability={note:string;timeZone:string;windows:{
  id:string;dayOfWeek:number;startMinute:number;endMinute:number;
  kind:'AVAILABLE'|'IF_NEEDED'}[]};
type Timesheet={staffName:string;timeZone:string;entries:{
  id:string;clockIn:string;clockOut:string|null;
  locationCode:string;locationName:string}[]};
type Page='shift-schedule'|'my-availability'|'timesheet';
const days=['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const roles=['Opening','Closing','Deep Clean','Weekly Duties','Non-Operational','Mid-shift',
  ...Array.from({length:16},(_,i)=>'Shift '+String(i+1).padStart(2,'0')+' - Onboarding')];
const pad=(n:number)=>String(n).padStart(2,'0');
function dayOffset(date:string,offset:number){
  const d=new Date(Date.parse(date+'T12:00:00Z')+offset*86400000);
  return d.toISOString().slice(0,10);
}
function zonedDate(date:Date,zone:string){
  const parts=new Intl.DateTimeFormat('en-US',{timeZone:zone,year:'numeric',
    month:'2-digit',day:'2-digit'}).formatToParts(date);
  const get=(key:string)=>parts.find(p=>p.type===key)?.value??'';
  return get('year')+'-'+get('month')+'-'+get('day');
}
function monday(date:string){
  const isoDay=new Date(date+'T12:00:00Z').getUTCDay();
  return dayOffset(date,-((isoDay+6)%7));
}
function localStamp(instant:string,timeZone:string){
  return new Intl.DateTimeFormat('en-US',{timeZone,hour:'numeric',minute:'2-digit',
    hour12:true}).format(new Date(instant)).replace(/\s/g,'').toLowerCase();
}
function minuteAt(instant:string,timeZone:string){
  const parts=new Intl.DateTimeFormat('en-US',{timeZone,hour:'2-digit',
    minute:'2-digit',hourCycle:'h23'}).formatToParts(new Date(instant));
  return Number(parts.find(p=>p.type==='hour')?.value??0)*60+
    Number(parts.find(p=>p.type==='minute')?.value??0);
}
function dateAt(instant:string,timeZone:string){return zonedDate(new Date(instant),timeZone);}
function duration(start:string,end:string){return (Date.parse(end)-Date.parse(start))/3600000;}
function hourLabel(m:number){
  const h=Math.floor(m/60),suffix=h<12?'am':'pm';
  return (h%12||12)+(m%60?':'+pad(m%60):'')+suffix;
}
function titleDate(date:string){
  return new Intl.DateTimeFormat('en-US',{month:'short',day:'numeric',year:'numeric',
    timeZone:'UTC'}).format(new Date(date+'T12:00:00Z'));
}
export function WorkforceWorkspace({location,page}:{location:Location;page:Page}){
  const [week,setWeek]=useState(()=>monday(zonedDate(new Date(),location.timezone)));
  const [from,setFrom]=useState(()=>dayOffset(zonedDate(new Date(),location.timezone),-14));
  const [to,setTo]=useState(()=>zonedDate(new Date(),location.timezone));
  const [selectedRole,setSelectedRole]=useState('Opening');
  const [shifts,setShifts]=useState<ShiftsResponse|null>(null);
  const [availability,setAvailability]=useState<Availability|null>(null);
  const [timesheet,setTimesheet]=useState<Timesheet|null>(null);
  const [selectedShift,setSelectedShift]=useState<Shift|null>(null);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState('');
  const [loading,setLoading]=useState(true);
  const [refresh,setRefresh]=useState(0);
  const [workingMessage,setWorkingMessage]=useState('');
  const [copyScope,setCopyScope]=useState<'ROLE'|'ALL'|null>(null);
  const [copyFrom,setCopyFrom]=useState(()=>dayOffset(monday(zonedDate(new Date(),location.timezone)),-7));
  const [staffChoices,setStaffChoices]=useState<StaffChoice[]>([]);
  const [staffQuery,setStaffQuery]=useState('');
  const [chosenAvailability,setChosenAvailability]=useState<AvailWindow|null>(null);
  const [drag,setDrag]=useState<Drag|null>(null);
  const dragRef=useRef<Drag|null>(null);
  const endWeek=dayOffset(week,6);
  useEffect(()=>{
    setWeek(monday(zonedDate(new Date(),location.timezone)));
    setFrom(dayOffset(zonedDate(new Date(),location.timezone),-14));
    setTo(zonedDate(new Date(),location.timezone));
    setSelectedShift(null);
  },[location.code,location.timezone]);
  useEffect(()=>{
    const ctrl=new AbortController();setLoading(true);setError('');
    const params=new URLSearchParams({location:location.code,
      ...(page==='shift-schedule'?{from:week,to:endWeek}:
        page==='timesheet'?{from,to}:{})});
    const route=page==='shift-schedule'?'shifts':
      page==='timesheet'?'timesheet':'availability';
    void fetch('/api/v1/staff/workforce/'+route+'?'+params,{
      credentials:'same-origin',cache:'no-store',signal:ctrl.signal,
    }).then(async response=>{
      if(!response.ok)throw Error('Workforce records are temporarily unavailable.');
      const body=await response.json() as {ok:boolean;data?:unknown};
      if(!body.ok||!body.data)throw Error('Workforce records are temporarily unavailable.');
      if(ctrl.signal.aborted)return;
      if(page==='shift-schedule')setShifts(body.data as ShiftsResponse);
      else if(page==='timesheet')setTimesheet(body.data as Timesheet);
      else setAvailability(body.data as Availability);
    }).catch(e=>{if(!ctrl.signal.aborted)
      setError(e instanceof Error?e.message:'Unable to load workforce data.');
    }).finally(()=>{if(!ctrl.signal.aborted)setLoading(false);});
    return ()=>ctrl.abort();
  },[page,location.code,week,endWeek,from,to,refresh]);
  const removeShift=async()=>{
    if(!selectedShift||busy||!shifts?.canManageShifts)return;
    setBusy(true);setError('');
    try{
      const r=await fetch('/api/v1/staff/workforce/shifts/remove',{
        method:'POST',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:location.code,shiftId:selectedShift.shiftId}),
      });
      const b=await r.json() as {ok:boolean;error?:{code:string}};
      if(!r.ok||!b.ok)throw Error('Could not remove shift.');
      setShifts(prev=>prev?{...prev,shifts:prev.shifts.filter(x=>
        x.shiftId!==selectedShift.shiftId)}:prev);
      setSelectedShift(null);
    }catch(e){setError(e instanceof Error?e.message:'Could not remove shift.');}
    finally{setBusy(false);}
  };

  // One calendar interaction makes a 15-minute block. Dragging or resizing
  // changes its end in 15-minute increments, then persists to PostgreSQL.
  const minuteInLane=(e:PointerEvent<HTMLElement>)=>{
    const rect=(e.currentTarget as HTMLElement).closest('.ecfs-week-lane')!
      .getBoundingClientRect();
    return Math.max(540,Math.min(1245,
      540+Math.floor((e.clientY-rect.top)/30)*15));
  };
  const applyDrag=(draft:Drag)=>{
    if(draft.kind==='shift'||draft.kind==='shift-resize'){
      const day=dayOffset(week,draft.dayIndex);
      const convert=(minute:number)=>localBookingTimeToUTC(
        day+'T'+pad(Math.floor(minute/60))+':'+pad(minute%60),location.timezone);
      const previous=draft.shift;
      void postAction('shifts/save',{
        roleName:previous?.roleName??selectedRole,
        actorId:previous?.actorId??null,
        ...(previous?{shiftId:previous.shiftId}:{}),
        startsAt:previous?.startsAt??convert(draft.startMinute),
        endsAt:convert(draft.endMinute),
      },previous?'Shift duration updated.':'Draft shift created.');
    }else{
      void postAction('availability/save',{
        ...(draft.window?{id:draft.window.id}:{}),
        dayOfWeek:draft.dayIndex,startMinute:draft.startMinute,
        endMinute:draft.endMinute,kind:draft.window?.kind??'AVAILABLE',
      },'Availability saved.');
    }
  };
  const beginDrag=(event:PointerEvent<HTMLElement>,
    kind:Drag['kind'],dayIndex:number,existing?:Shift|AvailWindow)=>{
    if(busy||loading)return;
    if((kind==='shift'||kind==='shift-resize')&&!shifts?.canManageShifts)return;
    if(kind==='shift'||kind==='availability'){
      if(event.currentTarget!==event.target)return;
    }
    event.preventDefault();
    const minute=minuteInLane(event);
    const prior=existing as Shift|AvailWindow|undefined;
    const starts=(kind==='shift-resize'
      ?minuteAt((prior as Shift).startsAt,location.timezone)
      :kind==='availability-resize'?(prior as AvailWindow).startMinute:minute);
    const ends=(kind.endsWith('resize')
      ?Math.max(starts+15,minute+15):minute+15);
    const next:Drag={kind,dayIndex,startMinute:starts,endMinute:ends,
      ...(kind==='shift-resize'?{shift:prior as Shift}:{}),
      ...(kind==='availability-resize'?{window:prior as AvailWindow}:{})};
    dragRef.current=next;setDrag(next);
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const moveDrag=(event:PointerEvent<HTMLElement>,dayIndex:number)=>{
    const current=dragRef.current;
    if(!current||current.dayIndex!==dayIndex)return;
    const minute=minuteInLane(event);
    const next=current.kind.endsWith('resize')
      ?{...current,endMinute:Math.max(current.startMinute+15,minute+15)}
      :{...current,startMinute:Math.min(current.startMinute,minute),
        endMinute:Math.max(current.endMinute,minute+15)};
    dragRef.current=next;setDrag(next);
  };
  const finishDrag=(event:PointerEvent<HTMLElement>,dayIndex:number)=>{
    const current=dragRef.current;
    if(!current||current.dayIndex!==dayIndex)return;
    if(event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    dragRef.current=null;setDrag(null);applyDrag(current);
  };
  const postAction=async(endpoint:string,data:Record<string,unknown>,success:string)=>{
    if(busy)return;
    setBusy(true);setError('');setWorkingMessage('');
    try{
      const response=await fetch('/api/v1/staff/workforce/'+endpoint,{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:location.code,...data}),
      });
      const body=await response.json() as {ok:boolean;
        data?:{copied?:number;published?:number};
        error?:{code?:string;message?:string}};
      if(!response.ok||!body.ok){
        const reason=body.error?.message??body.error?.code??'Could not save.';
        throw Error(reason.replaceAll('_',' ').toLowerCase());
      }
      setWorkingMessage(
        body.data?.copied!==undefined?body.data.copied+' shifts copied as drafts.':
        body.data?.published!==undefined?body.data.published+' shifts published.':success);
      setRefresh(x=>x+1);
    }catch(e){setError(e instanceof Error?e.message:'Unable to save.');}
    finally{setBusy(false);}
  };
  const publish=(scope:'SHIFT'|'ROLE_WEEK'|'WEEK'|'FUTURE',shiftId?:string)=>
    void postAction('shifts/publish',{
      scope,week, ...(scope==='ROLE_WEEK'?{roleName:selectedRole}:{}),
      ...(shiftId?{shiftId}:{}),
    },'Shifts published.');
  const copy=()=>{if(!copyScope)return;
    void postAction('shifts/copy',{
      scope:copyScope,sourceWeek:monday(copyFrom),targetWeek:week,
      ...(copyScope==='ROLE'?{roleName:selectedRole}:{}),
    },'Shifts copied.');setCopyScope(null);
  };
  useEffect(()=>{
    if(!selectedShift||!shifts?.canManageShifts)return;
    const ctrl=new AbortController();
    const query=new URLSearchParams({
      location:location.code,startsAt:selectedShift.startsAt,
      endsAt:selectedShift.endsAt,
    });
    void fetch('/api/v1/staff/workforce/shift-staff?'+query,{
      credentials:'same-origin',cache:'no-store',signal:ctrl.signal,
    }).then(async res=>{
      if(!res.ok)throw Error('Staff availability unavailable.');
      const body=await res.json() as {ok:boolean;data?:{members:StaffChoice[]}};
      if(body.ok&&Array.isArray(body.data?.members)&&!ctrl.signal.aborted)
        setStaffChoices(body.data.members);
    }).catch(()=>{if(!ctrl.signal.aborted)setStaffChoices([]);});
    return ()=>ctrl.abort();
  },[selectedShift?.shiftId,selectedShift?.startsAt,selectedShift?.endsAt,
    location.code,shifts?.canManageShifts]);
  const changeShift=(fields:{roleName?:string;actorId?:string|null})=>{
    if(!selectedShift||selectedShift.publishedAt)return;
    void postAction('shifts/save',{
      shiftId:selectedShift.shiftId,roleName:fields.roleName??selectedShift.roleName,
      actorId:fields.actorId===undefined?selectedShift.actorId:fields.actorId,
      startsAt:selectedShift.startsAt,endsAt:selectedShift.endsAt,
    },'Draft shift updated.');
    setSelectedShift(null);
  };
  const changeAvailability=(kind:'AVAILABLE'|'IF_NEEDED'|'NOT_AVAILABLE')=>{
    if(!chosenAvailability)return;
    void postAction('availability/save',{
      ...chosenAvailability,kind,
    },kind==='NOT_AVAILABLE'?'Availability removed.':'Availability updated.');
    setChosenAvailability(null);
  };
  const unpublishedRole=(shifts?.shifts??[]).filter(item=>
    item.roleName===selectedRole&&!item.publishedAt).length;
  const shiftData=useMemo(()=>shifts?.shifts.filter(s=>
    s.roleName===selectedRole)||[],[shifts,selectedRole]);
  const timeEntries=timesheet?.entries??[];
  const totalHours=timeEntries.reduce((total,r)=>total+(r.clockOut?
    duration(r.clockIn,r.clockOut):0),0);
  const csv=()=>{
    const header='Clock In,Clock Out,Hours,Project,Location';
    const rows=timeEntries.map(row=>[
      row.clockIn,row.clockOut??'',row.clockOut?
        duration(row.clockIn,row.clockOut).toFixed(2):'', '', row.locationName,
    ].map(x=>'"'+String(x).replaceAll('"','""')+'"').join(','));
    const url=URL.createObjectURL(new Blob([[header,...rows].join('\r\n')],{
      type:'text/csv;charset=utf-8'}));
    const link=document.createElement('a');link.href=url;
    link.download='ECFS_Timesheet_'+from+'_'+to+'.csv';link.click();
    URL.revokeObjectURL(url);
  };
  return <main className="ecfs-workforce" aria-label={page}>
    {page==='shift-schedule'?<>
      <div className="ecfs-workforce-shell">
        <aside className="ecfs-shift-roles" aria-label="Shift roles">
          <button type="button" className="ecfs-shift-jump"
            onClick={()=>setWeek(monday(zonedDate(new Date(),location.timezone)))}>
            jump to date</button>
          {roles.map(role=><button key={role} type="button"
            aria-pressed={selectedRole===role}
            onClick={()=>{setSelectedRole(role);setSelectedShift(null);}}>
            {role}</button>)}
          {shifts?.canManageShifts&&<div className="ecfs-shift-bulk">
            {unpublishedRole>0&&<button type="button" className="ecfs-publish-role"
              disabled={busy} onClick={()=>publish('ROLE_WEEK')}>
              Publish ({unpublishedRole}) {selectedRole}</button>}
            <button type="button" onClick={()=>setCopyScope('ROLE')}>
              Copy {selectedRole} Shifts</button>
            <button type="button" onClick={()=>setCopyScope('ALL')}>
              Copy ALL Shifts</button>
            <button type="button" onClick={()=>publish('WEEK')} disabled={busy}>
              Publish All THIS WEEK</button>
            <button type="button" onClick={()=>publish('FUTURE')} disabled={busy}>
              Publish ALL INCLUDING ALL FUTURE WEEKS</button>
            {copyScope&&<div className="ecfs-copy-shift" role="dialog"
              aria-label="Copy shifts">
              <strong>Copy {copyScope==='ALL'?'ALL':selectedRole} Shifts From</strong>
              <input type="date" aria-label="Copy Shifts From"
                value={copyFrom} onChange={e=>setCopyFrom(e.target.value)}/>
              <button type="button" disabled={busy||!copyFrom}
                onClick={copy} className="ecfs-publish-role">Copy Shifts Here</button>
              <button type="button" onClick={()=>setCopyScope(null)}>cancel</button>
            </div>}
          </div>}
        </aside>
        <div className="ecfs-shift-body">
          <div className="ecfs-workforce-titlebar">
            <h1>Shift Schedule</h1><span className="ecfs-workforce-subnav">
              <span>iCal</span> <span>shift roles</span> <span>shifts history</span>
              <span>vacations</span>
            </span>
          </div>
          <div className="ecfs-workforce-toolbar">
            <h2>{titleDate(week)} – {titleDate(endWeek)}</h2>
            <div><button type="button" onClick={()=>setWeek(
              monday(zonedDate(new Date(),location.timezone)))}>today</button>
              <button type="button" aria-label="Previous week"
                onClick={()=>{setWeek(dayOffset(week,-7));setSelectedShift(null);}}>‹</button>
              <button type="button" aria-label="Next week"
                onClick={()=>{setWeek(dayOffset(week,7));setSelectedShift(null);}}>›</button>
            </div>
          </div>
          {loading&&<p role="status">Loading shift schedule…</p>}
          {error&&<p role="alert">{error}</p>}
          {workingMessage&&<p role="status">{workingMessage}</p>}
          {!loading&&!error&&shifts&&<div className="ecfs-workforce-scroll">
            <div className="ecfs-week-grid" role="region" aria-label="Weekly shift grid">
              <div className="ecfs-week-corner"/>
              {days.map((d,i)=><div key={d} className="ecfs-week-day-head">
                {d} {Number(dayOffset(week,i).slice(8))}</div>)}
              <div className="ecfs-week-axis" style={{height:1440}}>
                {Array.from({length:25},(_,i)=><span key={i}
                  style={{top:i*60}}>{hourLabel(9*60+i*30)}</span>)}
              </div>
              {days.map((d,i)=>{
                const date=dayOffset(week,i);
                const daily=shiftData.filter(s=>dateAt(s.startsAt,location.timezone)===date);
                return <div key={d} className={'ecfs-week-lane'+
                  (i>=5?' is-weekend':'')} style={{height:1440}}
                  aria-label={d+' shift time slots'}
                  onPointerDown={e=>beginDrag(e,'shift',i)}
                  onPointerMove={e=>moveDrag(e,i)}
                  onPointerUp={e=>finishDrag(e,i)}>
                  {daily.map(s=>{
                    const top=Math.max(0,minuteAt(s.startsAt,location.timezone)-540)*2;
                    const height=Math.max(24,Math.min(1440-top,
                      duration(s.startsAt,s.endsAt)*120));
                    const overlap=daily.filter(x=>
                      Date.parse(x.startsAt)<Date.parse(s.endsAt)&&
                      Date.parse(x.endsAt)>Date.parse(s.startsAt));
                    const position=overlap.findIndex(x=>x.shiftId===s.shiftId);
                    const cols=Math.max(1,overlap.length);
                    return <button type="button" key={s.shiftId}
                      className="ecfs-shift-block" style={{top,height,
                        left:(position/cols*100)+'%',width:(100/cols)+'%',
                        backgroundColor:s.shiftColor??(s.actorId?'#277348':'#f0debb')}}
                      data-publication={s.publishedAt?'published':'draft'}
                      aria-label={s.staffName+' '+s.roleName+' '+localStamp(
                        s.startsAt,location.timezone)}
                      onPointerDown={e=>e.stopPropagation()}
                      onClick={()=>{setSelectedShift(s);setStaffChoices([]);}}>
                      <strong>{localStamp(s.startsAt,location.timezone)} – {
                        localStamp(s.endsAt,location.timezone)}</strong>
                      <b>{s.staffName}</b><span>({s.roleName})</span>
                      {!s.publishedAt&&<em>draft</em>}
                      {!s.publishedAt&&shifts?.canManageShifts&&
                        <span className="ecfs-shift-resizer"
                          aria-label="Resize shift"
                          onPointerDown={e=>{e.stopPropagation();
                            beginDrag(e,'shift-resize',i,s);}}
                          onPointerMove={e=>moveDrag(e,i)}
                          onPointerUp={e=>{e.stopPropagation();finishDrag(e,i);}}/>}
                    </button>;
                  })}
                  {drag?.dayIndex===i&&drag.kind.startsWith('shift')&&
                    <div className="ecfs-workforce-drag-preview" style={{
                      top:(drag.startMinute-540)*2,
                      height:(drag.endMinute-drag.startMinute)*2,
                    }}>{hourLabel(drag.startMinute)} - {hourLabel(drag.endMinute)} (draft)</div>}
                </div>;
              })}
            </div>
          </div>}
          {!loading&&!error&&shiftData.length===0&&
            <p className="ecfs-workforce-empty" role="status">
              No {selectedRole.toLowerCase()} shifts scheduled for this week.</p>}
          {selectedShift&&<section className="ecfs-shift-detail" role="dialog"
            aria-label="Shift details">
            <button className="ecfs-shift-detail-close" type="button"
              aria-label="Close shift details" onClick={()=>setSelectedShift(null)}>×</button>
            <p>{titleDate(dateAt(selectedShift.startsAt,location.timezone))}</p>
            <p>{localStamp(selectedShift.startsAt,location.timezone)} – {
              localStamp(selectedShift.endsAt,location.timezone)} ({
              duration(selectedShift.startsAt,selectedShift.endsAt).toFixed(2)}hrs)</p>
            {selectedShift.publishedAt&&<strong>Published</strong>}
            {shifts?.canManageShifts&&!selectedShift.publishedAt
              ?<label className="ecfs-shift-select-label">
                <strong>Shift Role</strong>
                <select aria-label="Shift role" value={selectedShift.roleName}
                  onChange={e=>changeShift({roleName:e.target.value})}>
                  {roles.map(role=><option key={role} value={role}>{role}</option>)}
                </select>
              </label>
              :<strong>{selectedShift.roleName} Role</strong>}
            {shifts?.canManageShifts&&!selectedShift.publishedAt
              ?<label className="ecfs-shift-select-label">
                <span>Staffed by</span>
                <input aria-label="Find available staff" placeholder="Search"
                  value={staffQuery} onChange={e=>setStaffQuery(e.target.value)}/>
                <select aria-label="Staffed by" value={selectedShift.actorId??''}
                  onChange={e=>changeShift({actorId:e.target.value||null})}>
                  <option value="">No Staff Assigned</option>
                  {(['AVAILABLE','IF_NEEDED','UNAVAILABLE'] as const).map(status=>
                    <optgroup key={status} label={status==='AVAILABLE'?'Available':
                      status==='IF_NEEDED'?'Available if Needed':'Unavailable'}>
                      {staffChoices.filter(m=>m.status===status&&
                        m.name.toLowerCase().includes(staffQuery.toLowerCase())).map(m=>
                        <option value={m.actorId} key={m.actorId}>{m.name}</option>)}
                    </optgroup>)}
                </select>
              </label>
              :<p>Staffed by {selectedShift.staffName}</p>}
            {staffChoices.some(m=>m.actorId===selectedShift.actorId&&
              m.status==='IF_NEEDED')&&<p>☹ Prefers to work at other times.</p>}
            {staffChoices.some(m=>m.actorId===selectedShift.actorId&&
              m.status==='UNAVAILABLE')&&<p>⚠ Outside preferred availability.</p>}
            {shifts?.canManageShifts&&!selectedShift.publishedAt&&
              <button type="button" disabled={busy} onClick={()=>
                publish('SHIFT',selectedShift.shiftId)}>Publish Shift</button>}
            {shifts?.canManageShifts&&<button type="button" disabled={busy}
              onClick={()=>void removeShift()}>Remove Shift</button>}
          </section>}
        </div>
      </div>
    </>:page==='my-availability'?<>
      <div className="ecfs-workforce-titlebar"><h1>My Availability</h1>
        <span>use your personal calendar to block your availability at {location.name}</span>
      </div>
      {loading&&<p role="status">Loading availability…</p>}
      {error&&<p role="alert">{error}</p>}
      {workingMessage&&<p role="status">{workingMessage}</p>}
      {!loading&&!error&&availability&&<>
        {availability.note&&<div className="ecfs-availability-note">{availability.note}</div>}
        <div className="ecfs-workforce-scroll">
          <div className="ecfs-week-grid" aria-label="Weekly availability grid">
            <div className="ecfs-week-corner"/>
            {days.map(d=><div className="ecfs-week-day-head" key={d}>{d}</div>)}
            <div className="ecfs-week-axis" style={{height:1440}}>
              {Array.from({length:49},(_,i)=><span key={i}
                style={{top:i*30}}>{hourLabel(9*60+i*15)}</span>)}
            </div>
            {days.map((d,i)=><div key={d} className={'ecfs-week-lane'+
              (i>=5?' is-weekend':'')} style={{height:1440}}
              aria-label={d+' availability time slots'}
              onPointerDown={e=>beginDrag(e,'availability',i)}
              onPointerMove={e=>moveDrag(e,i)}
              onPointerUp={e=>finishDrag(e,i)}>
              {availability.windows.filter(w=>w.dayOfWeek===i).map(w=>
                <div role="button" tabIndex={0} key={w.id}
                  aria-label={d+' '+hourLabel(w.startMinute)+' '+(w.kind==='IF_NEEDED'?
                    'Available if Needed':'Available')}
                  className={'ecfs-availability-block '+(w.kind==='IF_NEEDED'?
                    'if-needed':'')} style={{
                    top:Math.max(0,w.startMinute-540)*2,
                    height:Math.max(24,Math.min(1440-(w.startMinute-540)*2,
                      (w.endMinute-w.startMinute)*2))}}
                  onPointerDown={e=>e.stopPropagation()}
                  onClick={()=>setChosenAvailability(w)}
                  onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){
                    e.preventDefault();setChosenAvailability(w);}}}>
                  <strong>{hourLabel(w.startMinute)} - {hourLabel(w.endMinute)}</strong>
                  <b>{w.kind==='IF_NEEDED'?'Available if Needed':'Available'}</b>
                  <span className="ecfs-availability-resizer"
                    aria-label="Resize availability"
                    onPointerDown={e=>{e.stopPropagation();
                      beginDrag(e,'availability-resize',i,w);}}
                    onPointerMove={e=>moveDrag(e,i)}
                    onPointerUp={e=>{e.stopPropagation();finishDrag(e,i);}}/>
                </div>)}
              {drag?.dayIndex===i&&drag.kind.includes('availability')&&
                <div className="ecfs-workforce-drag-preview" style={{
                  top:(drag.startMinute-540)*2,
                  height:(drag.endMinute-drag.startMinute)*2,
                }}>{hourLabel(drag.startMinute)} - {hourLabel(drag.endMinute)} Available</div>}
            </div>)}
          </div>
        </div>
        {!availability.windows.length&&<p className="ecfs-workforce-empty">
          No weekly availability has been entered for this account at {location.name}.</p>}
        {chosenAvailability&&<section className="ecfs-availability-popover"
          role="dialog" aria-label="This time is">
          <button type="button" aria-label="Close availability options"
            className="ecfs-shift-detail-close"
            onClick={()=>setChosenAvailability(null)}>×</button>
          <h3>This time is...</h3>
          <button type="button" className="ecfs-av-choice available"
            disabled={busy} onClick={()=>changeAvailability('AVAILABLE')}>
            Available</button>
          <button type="button" className="ecfs-av-choice if-needed"
            disabled={busy} onClick={()=>changeAvailability('IF_NEEDED')}>
            Available if Needed</button>
          <button type="button" className="ecfs-av-choice unavailable"
            disabled={busy} onClick={()=>changeAvailability('NOT_AVAILABLE')}>
            Not Available</button>
        </section>}
      </>}
    </>:<>
      <div className="ecfs-workforce-titlebar"><h1>Timesheet</h1></div>
      <div className="ecfs-timesheet-controls">
        <label>From <input type="date" value={from} onChange={e=>setFrom(e.target.value)}/></label>
        <label>To <input type="date" value={to} onChange={e=>setTo(e.target.value)}/></label>
        <span>{location.name}</span>
        <button type="button" onClick={csv} disabled={!timeEntries.length}>CSV</button>
      </div>
      {loading&&<p role="status">Loading timesheet…</p>}
      {error&&<p role="alert">{error}</p>}
      {!loading&&!error&&timesheet&&<>
        <h2>{timesheet.staffName}</h2>
        <div className="ecfs-workforce-scroll"><table className="ecfs-timesheet-table">
          <thead><tr><th>Clock In</th><th>Clock Out</th><th>Hours</th>
            <th>Project</th><th>Location</th></tr></thead>
          <tbody>{timeEntries.map(e=><tr key={e.id}>
            <td>{new Date(e.clockIn).toLocaleString('en-US',{timeZone:location.timezone,
              weekday:'short',month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</td>
            <td>{e.clockOut?new Date(e.clockOut).toLocaleString('en-US',{
              timeZone:location.timezone,weekday:'short',month:'short',day:'numeric',
              hour:'2-digit',minute:'2-digit'}):'Clocked in'}</td>
            <td>{e.clockOut?duration(e.clockIn,e.clockOut).toFixed(2):'—'}</td>
            <td>—</td><td>{e.locationName}</td>
          </tr>)}</tbody>
          <tfoot><tr><td colSpan={2}/><td colSpan={3}>
            Total Hours: {totalHours.toFixed(2)}</td></tr></tfoot>
        </table></div>
        {!timeEntries.length&&<p className="ecfs-workforce-empty">
          No time-clock entries for this date range.</p>}
      </>}
    </>}
  </main>;
}
